import { CSSProperties, FC, ReactNode, useEffect, useRef } from 'react';

import { useParams } from 'react-router-dom';

import useTheme from 'api/useTheme';
import { PREVIEW_CONTAINER_HEIGHT } from 'views/OfferDesign/OfferDesign.consts';
import {
  applyCssVariables,
  clearCssVariables
} from 'views/OfferDesign/pages/OfferDesignForm/components/OfferDesignPreview/OfferDesignPreview.utils';

import { GeneralThemeFormValues } from 'components/Theme/GeneralSettings/generalTheme.types';
import { StoreThemeFormValues } from 'components/Theme/StoreTheme/storeTheme.types';

export interface OfferPreviewThemeContext {
  generalTheme: GeneralThemeFormValues;
  storeScreen: StoreThemeFormValues;
}

interface OfferPreviewShellProps {
  width?: number;
  containerStyle?: CSSProperties;
  containerClassName?: string;
  containerId?: string;
  wrapperClassName?: string;
  children: (theme: OfferPreviewThemeContext) => ReactNode;
}

const DEFAULT_PREVIEW_WIDTH = 375;
const DEFAULT_WRAPPER_CLASS =
  'w-1/2 h-full bg-zinc-100 px-16 pt-9 pb-8 overflow-y-auto';

/**
 * Shared shell for offer preview panels.
 *
 * Owns the responsibilities common to every offer preview:
 *  - Fetching general theme + store screen
 *  - Wiring CSS variables (button color, font) onto the inner container
 *  - Rendering the standard zinc-100 wrapper + centered preview frame
 *  - Gating render until theme data is loaded
 *
 * Each preview only declares its own data + the JSX of the design itself.
 */
export const OfferPreviewShell: FC<OfferPreviewShellProps> = ({
  width = DEFAULT_PREVIEW_WIDTH,
  containerStyle,
  containerClassName,
  containerId,
  wrapperClassName = DEFAULT_WRAPPER_CLASS,
  children
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { publisherId } = useParams();
  const { getGeneralTheme: generalTheme, getStoreScreen: storeScreen } =
    useTheme(publisherId);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !storeScreen.data) return;

    applyCssVariables(el, storeScreen.data, generalTheme.data?.font || '');
    return () => clearCssVariables(el);
  }, [storeScreen.data, generalTheme.data?.font]);

  if (
    generalTheme.isLoading ||
    storeScreen.isLoading ||
    !generalTheme.data ||
    !storeScreen.data
  ) {
    return null;
  }

  const mergedStyle: CSSProperties = {
    width,
    position: 'relative',
    ...containerStyle
  };

  return (
    <div className={wrapperClassName}>
      <div
        className="flex justify-center items-center"
        style={{ minHeight: PREVIEW_CONTAINER_HEIGHT }}
      >
        <div
          id={containerId}
          ref={containerRef}
          style={mergedStyle}
          className={containerClassName}
        >
          {children({
            generalTheme: generalTheme.data,
            storeScreen: storeScreen.data
          })}
        </div>
      </div>
    </div>
  );
};
